/**
 * Pro plan pricing (SP2, cue-forge-332.6; repriced cue-forge-sdv.2). CueForge Pro
 * is sold through Lemon Squeezy (Merchant of Record) as an annual subscription
 * with an optional monthly door. Prices and the hosted-checkout URLs are
 * build-time configurable so the live Lemon Squeezy values drop in without code
 * changes; until a checkout URL is set the UI falls back to the "enter your
 * license key" path only.
 *
 * Defaults: USD 99/year and USD 12/month (raised from $59 — $59 read as hobbyware
 * to broadcast pros; see the competitive-viability research). Override with
 * VITE_PRO_PRICE_USD / VITE_MONTHLY_PRICE_USD. Set VITE_CHECKOUT_URL (annual) and
 * VITE_MONTHLY_CHECKOUT_URL (monthly) once each Lemon Squeezy variant is live —
 * the monthly option only renders when its URL is set, so it never shows a dead
 * link. VITE_CUSTOMER_PORTAL_URL is the manage-subscription link, and
 * VITE_TRIAL_DAYS mirrors the LS free-trial length (0/unset ⇒ no trial copy).
 */
const DEFAULT_ANNUAL_PRICE_USD = 99;
const DEFAULT_MONTHLY_PRICE_USD = 12;

function readEnvPrice(raw: string | undefined, fallback: number): number {
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function readPrice(): number {
  return readEnvPrice(
    import.meta.env.VITE_PRO_PRICE_USD as string | undefined,
    DEFAULT_ANNUAL_PRICE_USD,
  );
}

function readMonthlyPrice(): number {
  return readEnvPrice(
    import.meta.env.VITE_MONTHLY_PRICE_USD as string | undefined,
    DEFAULT_MONTHLY_PRICE_USD,
  );
}

/** Free-trial length (days) from VITE_TRIAL_DAYS; 0 (the default) ⇒ no trial. */
function readTrialDays(): number {
  const raw = import.meta.env.VITE_TRIAL_DAYS as string | undefined;
  const n = raw !== undefined ? Number(raw) : NaN;
  return Number.isInteger(n) && n > 0 ? n : 0;
}

export interface ProPlan {
  name: string;
  annualPriceUsd: number;
  /** Human label, e.g. "$99/year". */
  priceLabel: string;
  /** Monthly price in USD (the burst/project door). */
  monthlyPriceUsd: number;
  /** Human label, e.g. "$12/month". */
  monthlyPriceLabel: string;
  /** Lemon Squeezy hosted checkout URL for the annual variant; empty until configured. */
  checkoutUrl: string;
  /** Lemon Squeezy hosted checkout URL for the monthly variant; empty until configured. */
  monthlyCheckoutUrl: string;
  /** Whether the monthly option is live (its checkout URL is set) and should render. */
  hasMonthly: boolean;
  /**
   * Lemon Squeezy customer-portal URL for managing the subscription (update
   * payment, cancel, resume); empty until configured.
   */
  manageUrl: string;
  /** Free-trial length in days mirrored from LS; 0 ⇒ no trial. */
  trialDays: number;
  /** Human trial label, e.g. "7-day free trial", or '' when there is no trial. */
  trialLabel: string;
  features: string[];
}

/** Format a whole-dollar annual price as a compact label. */
export function formatAnnualPrice(usd: number): string {
  const amount = Number.isInteger(usd) ? String(usd) : usd.toFixed(2);
  return `$${amount}/year`;
}

/** Format a whole-dollar monthly price as a compact label. */
export function formatMonthlyPrice(usd: number): string {
  const amount = Number.isInteger(usd) ? String(usd) : usd.toFixed(2);
  return `$${amount}/month`;
}

/** Format a free-trial label, e.g. "7-day free trial"; '' when there is no trial. */
export function formatTrialLabel(days: number): string {
  return days > 0 ? `${days}-day free trial` : '';
}

/**
 * Trial-aware primary call-to-action: "Start your 7-day free trial" when a trial
 * is configured, otherwise "Get CueForge Pro — $99/year".
 */
export function proCtaLabel(
  plan: Pick<ProPlan, 'name' | 'priceLabel' | 'trialDays' | 'trialLabel'>,
): string {
  return plan.trialDays > 0
    ? `Start your ${plan.trialLabel}`
    : `Get ${plan.name} — ${plan.priceLabel}`;
}

const MONTHLY_CHECKOUT_URL =
  (import.meta.env.VITE_MONTHLY_CHECKOUT_URL as string | undefined)?.trim() ?? '';

export const PRO_PLAN: ProPlan = {
  name: 'CueForge Pro',
  annualPriceUsd: readPrice(),
  priceLabel: formatAnnualPrice(readPrice()),
  monthlyPriceUsd: readMonthlyPrice(),
  monthlyPriceLabel: formatMonthlyPrice(readMonthlyPrice()),
  checkoutUrl: (import.meta.env.VITE_CHECKOUT_URL as string | undefined)?.trim() ?? '',
  monthlyCheckoutUrl: MONTHLY_CHECKOUT_URL,
  hasMonthly: MONTHLY_CHECKOUT_URL !== '',
  manageUrl: (import.meta.env.VITE_CUSTOMER_PORTAL_URL as string | undefined)?.trim() ?? '',
  trialDays: readTrialDays(),
  trialLabel: formatTrialLabel(readTrialDays()),
  features: [
    'Broadcast formats: TTML, SCC, STL, iTT, MCC (CEA-708), EBU-TT-D (import + export)',
    'Video/waveform Studio: load a video, drag cues against the audio, frame-accurate retiming',
    'On-device transcription: draft cues from video or audio in your browser, language auto-detected, nothing uploads',
    'Custom QC presets: clone or build from scratch, import files your team shares',
    'Pro sync: frame-rate conversion, drift correction, audio auto-resync',
    'Batch processing, broadcast sources included (convert + QC many files at once)',
  ],
};
